import React, { useEffect, useState } from 'react'
import {
	RuleReq,
	expectedKeys,
	OptionsRes,
	RulesRes,
	translatedKeys,
	Variant,
	PostOptionReq,
	DeleteOptionReq,
} from '../types'
import {
	Badge,
	Form,
	OverlayTrigger,
	Spinner,
	Table,
	Tooltip,
} from 'react-bootstrap'
import OtherVariant from './OtherVariant'
import Button from 'react-bootstrap/Button'
import { langs, otherVariantsLimit } from '../constants'
import { ReactComponent as DeleteIcon } from '../assets/icons/trash3-fill.svg'
import DeleteRuleModal from './DeleteRuleModal'
import DeleteOptionModal from './DeleteOptionModal'
import useHasPermission from '../hooks/useHasPermission'

interface Props {
	type: keyof typeof expectedKeys
	rules: RulesRes
	options: OptionsRes
	fetchDeleteRule: (req: RuleReq) => void
	fetchPostOption: (req: PostOptionReq) => void
	fetchDeleteOption: (req: DeleteOptionReq) => void
	isLoadingDeleteOption: boolean
	isLoadingDeleteRule: boolean
	isLoadingPostOption: boolean
	isSuccessDeleteRule: boolean
	isSuccessDeleteOption: boolean
	isSuccessPostOption: boolean
}

const EditTable: React.FC<Props> = ({
	type,
	options,
	rules,
	fetchDeleteRule,
	fetchDeleteOption,
	isLoadingDeleteOption,
	isLoadingDeleteRule,
	isSuccessDeleteRule,
	isSuccessDeleteOption,
	isLoadingPostOption,
	isSuccessPostOption,
	fetchPostOption,
}) => {
	const [editVariantCode, setEditVariantCode] = useState<string | null>(null)
	const [editRuleCode, setEditRuleCode] = useState<string | null>(null)
	const [otherVariantsValue, setOtherVariantsValue] = useState<
		Variant[] | null
	>(null)
	const [rulesValue, setRulesValue] = useState<{
		[key: string]: string[]
	} | null>(null)
	const [deletedRuleString, setDeletedRuleString] = useState('')
	const [deleteReq, setDeleteReq] = useState<RuleReq | null>(null)
	const [deleteOptionReq, setDeleteOptionReq] =
		useState<DeleteOptionReq | null>(null)
	const [optionString, setOptionString] = useState('')
	const [partNameInput, setPartNameInput] = useState('')
	const [codeInput, setCodeInput] = useState('')

	useEffect(() => {
		if (editVariantCode) {
			setOtherVariantsValue(options[type][editVariantCode].other_variants)
			setPartNameInput(options[type][editVariantCode].part_name)
			setCodeInput(editVariantCode)
		}
	}, [editVariantCode])

	useEffect(() => {
		if (editRuleCode) {
			if (rules[type] && rules[type][editRuleCode]) {
				setRulesValue(rules[type][editRuleCode])
			}
		}
	}, [editRuleCode])

	const cancelVariantEdit = () => {
		setEditVariantCode(null)
		setOtherVariantsValue(null)
	}

	const cancelRuleEdit = () => {
		setEditRuleCode(null)
		setRulesValue(null)
	}

	const setEditVariant = (code: string) => {
		setEditVariantCode(code)
		cancelRuleEdit()
	}

	const setEditRule = (code: string) => {
		setEditRuleCode(code)
		cancelVariantEdit()
	}

	useEffect(() => {
		if (!isLoadingDeleteRule && isSuccessDeleteRule) {
			setEditRuleCode(null)
			setDeletedRuleString('')
		}
	}, [isLoadingDeleteRule])

	useEffect(() => {
		if(!isLoadingDeleteOption && isSuccessDeleteOption) {
			setOptionString('')
			setDeleteOptionReq(null)
		}
	}, [isLoadingDeleteOption])

	const handleSave = () => {
		if (editVariantCode && otherVariantsValue) {
			const req: PostOptionReq = {
				params: {
					option: type,
					option_code: editVariantCode,
				},
				data: {
					code: codeInput,
					name: partNameInput,
					otherVariants: otherVariantsValue
						.filter((i) => i.part_name !== '')
						.map((i) => ({ name: i.part_name, lang: i.lang })),
				},
			}
			fetchPostOption(req)
		}
	}
	useEffect(() => {
		if (!isLoadingPostOption && isSuccessPostOption) {
			setEditVariantCode(null)
			setOtherVariantsValue(null)
		}
	}, [isLoadingPostOption])

	const handleDeleteRule = (deleteReq: RuleReq) => {
		setDeletedRuleString(
			// @ts-ignore
			`${translatedKeys[deleteReq.selected_field]}: ${deleteReq.field_field_value} → ${translatedKeys[deleteReq.related_field]}: ${deleteReq.related_field_value}`,
		)
		setDeleteReq(deleteReq)
	}
	
	const handleDelete = (code: string) => {
		setOptionString(`${translatedKeys[type]}: ${code} (${options[type][code].part_name})`)
		setDeleteOptionReq({
			option: type,
			option_code: code,
		})
	}

	const hasOptionsUpdatePermission = useHasPermission('options.items.update')
	const hasOptionsDestroyPermission = useHasPermission('options.items.destroy')
	const hasRulesDestroyPermission = useHasPermission('rules.destroy')

	return (
		<Table striped bordered hover>
			<thead>
				<tr>
					<th>#</th>
					<th>Код</th>
					<th>Назва</th>
					<th className="w-50">Варіанти</th>
					<th></th>
					<th className="w-50">Правила</th>
				</tr>
			</thead>
			<tbody>
				{Object.keys(options[type]).map((code, index) => (
					<tr key={code}>
						<td>{index + 1}</td>
						<td>
							{editVariantCode === code ? (
								<Form.Control
									size="sm"
									value={codeInput}
									onChange={(e) => setCodeInput(e.target.value)}
								/>
							) : (
								code
							)}
						</td>
						<td>
							{editVariantCode === code ? (
								<Form.Control
									size="sm"
									value={partNameInput}
									onChange={(e) => setPartNameInput(e.target.value)}
								/>
							) : (
								options[type][code].part_name
							)}
						</td>
						<td>
							{editVariantCode === code ? (
								<div
									style={{
										display: 'flex',
										flexWrap: 'wrap',
										flexDirection: 'column',
									}}
								>
									{otherVariantsValue &&
										otherVariantsValue.map((variant) => (
											<div
												className="d-flex align-items-center gap-1 p-1"
												key={variant.full_name}
											>
												<Form.Select
													style={{ width: '80px' }}
													size="sm"
													value={variant.lang}
													onChange={(e) => {
														const newLang = e.target.value
														const newVariants = otherVariantsValue.map((v) => {
															if (v.full_name === variant.full_name) {
																return {
																	...v,
																	lang: newLang,
																}
															} else {
																return v
															}
														})
														setOtherVariantsValue(newVariants)
													}}
												>
													{langs.map((lang) => (
														<option key={lang} value={lang}>
															{lang}
														</option>
													))}
												</Form.Select>
												<Form.Control
													size="sm"
													value={variant.part_name}
													onChange={(e) => {
														const newPartName = e.target.value
														setOtherVariantsValue((prevVariants) => {
															if (prevVariants) {
																return prevVariants.map((v) =>
																	v.full_name === variant.full_name
																		? { ...v, part_name: newPartName }
																		: v,
																)
															}
															return prevVariants
														})
													}}
												/>
												<Button
													variant="danger"
													size="sm"
													onClick={() => {
														const newVariants = otherVariantsValue.filter(
															(v) => v.full_name !== variant.full_name,
														)
														setOtherVariantsValue(newVariants)
													}}
												>
													Видалити
												</Button>
											</div>
										))}
									{otherVariantsValue &&
										otherVariantsValue.length < otherVariantsLimit && (
										<Button
											className="m-1"
											size="sm"
											onClick={() => {
												const newVariants = [
													...otherVariantsValue,
													{
														lang: langs[0],
														full_name: `new ${otherVariantsValue.length + 1}`,
														part_name: '',
													},
												]
												setOtherVariantsValue(newVariants)
											}}
										>
												Додати
										</Button>
									)}
								</div>
							) : (
								<div
									style={{
										display: 'flex',
										flexWrap: 'wrap',
									}}
								>
									{options[type][code].other_variants.map((variant) => (
										<OtherVariant data={variant} key={variant.full_name} />
									))}
								</div>
							)}
						</td>
						<td>
							{editVariantCode === null ? (
								<div
									className={'w-100 d-flex flex-column gap-2 p-1'}
								>
									{hasOptionsDestroyPermission && (
										<Button
											size="sm"
											variant="danger"
											onClick={() => handleDelete(code)}
										>
											Видалити
										</Button>
									)}
									{hasOptionsUpdatePermission && (
										<Button
											size="sm"
											onClick={() => setEditVariant(code)}
											variant={'secondary'}
										>
											Редагувати
										</Button>
									)}
								</div>
							) : editVariantCode === code ? (
								<div
									className={'w-100 d-flex flex-column gap-2 p-1'}
								>
									<Button
										size="sm"
										variant="danger"
										onClick={cancelVariantEdit}
									>
										Відмінити
									</Button>
									<Button
										disabled={isLoadingPostOption}
										size="sm"
										variant="success"
										onClick={handleSave}
									>
										Зберегти{' '}
										{isLoadingPostOption && (
											<Spinner animation="border" size="sm" />
										)}
									</Button>
								</div>
							) : (
								<></>
							)}
						</td>
						<td>
							{rules[type] && rules[type][code]
								? Object.keys(rules[type][code]).map((ruleType) => {
									const ruleTypeKey = ruleType as keyof typeof translatedKeys
									return (
										<div key={ruleType}>
											{translatedKeys[ruleTypeKey]}:{' '}
											{rules[type][code][ruleType].map((ruleCode) => (
												<OverlayTrigger
													key={ruleCode}
													placement="top"
													delay={{
														show: 250,
														hide: 400,
													}}
													overlay={(props) => (
														<Tooltip id="tooltip" {...props}>
															{options[ruleTypeKey][ruleCode]?.part_name ?? `помилка коду (${ruleCode})`}
														</Tooltip>
													)}
												>
													<Badge
														onClick={() => {
															if (editRuleCode === code && hasRulesDestroyPermission) {
																setDeletedRuleString(ruleCode)
																handleDeleteRule({
																	selected_field: type,
																	field_field_value: code,
																	related_field: ruleTypeKey,
																	related_field_value: ruleCode,
																})
															}
														}}
														style={{
															cursor: editRuleCode === code && hasRulesDestroyPermission ? 'pointer' : 'default',
														}}
														bg={editRuleCode === code ? 'danger' : 'primary'}
														className={'m-1'}
													>
														<div className={'d-flex align-items-center'}>
															{ruleCode}
															{editRuleCode === code ? <DeleteIcon /> : <></>}
														</div>
													</Badge>
												</OverlayTrigger>
											))}
										</div>
									)
								})
								: ''}
							{editRuleCode !== code && rules[type] && rules[type][code] && hasRulesDestroyPermission ? (
								<Button
									size="sm"
									onClick={() => setEditRule(code)}
									variant={'secondary'}
								>
									Редагувати
								</Button>
							) : (
								<></>
							)}
						</td>
					</tr>
				))}
			</tbody>
			<DeleteRuleModal
				rule={deletedRuleString}
				onClose={() => setDeletedRuleString('')}
				isLoading={isLoadingDeleteRule}
				handleSubmit={() => {
					if (deleteReq) {
						fetchDeleteRule(deleteReq)
					}
				}}
			/>
			<DeleteOptionModal
				optionString={optionString}
				onClose={() => setOptionString('')}
				isLoading={isLoadingDeleteOption}
				handleSubmit={() => {
					if (deleteOptionReq) {
						fetchDeleteOption(deleteOptionReq)
					}
				}}
			/>
		</Table>
	)
}

export default EditTable
